import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import { PageHeading, Spinner } from "@prequel/shared-components";

import WebhookDetails from "../WebhookDetails";
import WebhookForm from "../WebhookForm";

import { useWebhook } from "@/api/queries/webhooks";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import BufoPage from "@/pages/BufoPage";
import { isBufoUUID } from "@/pages/BufoPage/bufo";
import { ExistingWebhook } from "@/types";

type WebhookContext = {
  webhook: ExistingWebhook;
};

// Parent wrapper: Loads data once, provides via context
const WebhookPage = () => {
  const webhookID = useRequiredParam("webhookID");
  const { data: webhook, isLoading } = useWebhook(webhookID);

  // Check for easter egg before rendering
  if (isBufoUUID(webhookID)) {
    return <BufoPage />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (!webhook) {
    return <Navigate to="/webhooks" replace />;
  }

  return <Outlet context={{ webhook }} />;
};

// Details page: Full page component, gets data from context
export const WebhookDetailsPage = () => {
  const navigate = useNavigate();
  const { webhook } = useOutletContext<WebhookContext>();

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={webhook.name}
        onGoBack={() => navigate("/webhooks")}
        breadcrumbs={<Breadcrumbs dynamicLabel={webhook.name} />}
      />
      <div className="flex-1">
        <div className="relative mx-auto max-w-4xl">
          <div className="pt-0 pb-16">
            <div className="pb-6">
              <WebhookDetails webhook={webhook} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit page: Full page component, gets data from context
export const WebhookEditPage = () => {
  const navigate = useNavigate();
  const { webhook } = useOutletContext<WebhookContext>();
  const webhookID = webhook.id;

  return (
    <div className="max-w-2xl px-7 py-4">
      <PageHeading
        title="Edit Webhook"
        onGoBack={() => navigate(`/webhooks/${webhookID}`)}
        breadcrumbs={<Breadcrumbs dynamicLabel={webhook.name} />}
      />
      <WebhookForm initialWebhook={webhook} />
    </div>
  );
};

export default WebhookPage;
